/**
 * SCWT-PM-V2 Phase 7 · Slice E — Landlord Water Health enrolment.
 *
 * Route: /landlord/water-health/enrol?property_id=…
 *
 * Flow:
 *   1. Fetch eligibility (verifies landlord-property link + no active row)
 *   2. Present benefits, $79/yr terms, cancellation policy, discount rule
 *   3. Consent checkbox — must be ticked before checkout
 *   4. POST /checkout → redirect to Stripe Checkout URL
 *   5. Stripe success returns to /landlord/water-health/success (informational only)
 *
 * Feature flag gated. Missing Stripe Price ID renders the controlled
 * "Enrolment is not currently available" panel — never leaks env or
 * Stripe error text.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, CheckCircle2, XCircle, Info } from "lucide-react";
import axios from "axios";
import { useFeature } from "@/lib/useFeature";
import { centsToAud } from "@/lib/lwhStatus";

const base = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${base}/api`, withCredentials: true });

function LandlordShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto">{children}</div>
    </div>
  );
}

export default function LandlordWaterHealthEnrol() {
  const [params]            = useSearchParams();
  const navigate            = useNavigate();
  const propertyId          = params.get("property_id") || "";
  const cancelled           = params.get("cancelled") === "1";
  const { isVisible: flagOn, loading: flagLoading } = useFeature("landlord_water_health_program");
  const [eligibility, setElig] = useState(null);
  const [loading, setLoading]  = useState(true);
  const [error, setError]      = useState(null);
  const [consent, setConsent]  = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!flagOn || !propertyId) return;
    setLoading(true); setError(null);
    api.get(`/landlord-portal/water-health/eligibility?property_id=${encodeURIComponent(propertyId)}`)
      .then(({ data }) => setElig(data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not check eligibility."))
      .finally(() => setLoading(false));
  }, [flagOn, propertyId]);

  const stripeConfigured = eligibility?.pricing?.stripe_configured;
  const price = eligibility?.pricing;

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const { data } = await api.post("/landlord-portal/water-health/checkout", {
        property_id: propertyId,
        origin_url: window.location.origin,
        consent_confirmed: true,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail
        : (detail?.detail || "Could not start checkout. Please try again."));
      setSubmitting(false);
    }
  };

  if (flagLoading) return <LandlordShell><div className="p-8">Loading…</div></LandlordShell>;
  if (!flagOn) {
    return <LandlordShell>
      <UnavailablePanel testId="lwh-enrol-flag-off" />
    </LandlordShell>;
  }

  return (
    <LandlordShell>
      <div data-testid="lwh-enrol-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700 mb-2">
          <ShieldCheck className="w-3.5 h-3.5" /> AWARE Water Health Program
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
          Enrol your property
        </h1>

        {cancelled && (
          <div data-testid="lwh-enrol-cancelled-notice"
                className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            Checkout was cancelled. You can review the terms and try again below.
          </div>
        )}

        {loading && <div className="mt-6 text-sm text-slate-500">Loading…</div>}
        {!loading && !stripeConfigured && (
          <UnavailablePanel testId="lwh-enrol-not-configured" />
        )}

        {!loading && stripeConfigured && eligibility && !eligibility.eligible && (
          <div data-testid="lwh-enrol-not-eligible"
                className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            This property already has a membership ({eligibility.existing_status?.replace(/_/g, " ")}). Manage it from your portal.
            <button className="ml-3 underline font-semibold" onClick={() => navigate("/landlord/portal")}
                    data-testid="lwh-enrol-back-btn">Back to portal</button>
          </div>
        )}

        {!loading && stripeConfigured && eligibility?.eligible && (
          <>
            <div className="mt-6 rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 shadow-sm">
              <div className="flex items-baseline gap-2">
                <span data-testid="lwh-enrol-price"
                       className="text-3xl font-bold text-slate-900">
                  {centsToAud(price.amount_cents)}
                </span>
                <span className="text-sm text-slate-500">per property per year</span>
              </div>
              <p className="mt-1.5 text-sm text-slate-600">
                Laboratory water tests are charged separately when a retest is due.
              </p>

              <div className="mt-5 grid gap-2 text-sm">
                {[
                  "Annual retest reminders + pre-filled booking link",
                  "Water Health Record: test history + timeline for this property",
                  "Priority booking support",
                  "Secure re-download of past reports and COAs",
                  `${price.member_discount_pct}% member discount on eligible retail lab tests for this property`,
                  "Optional reminder copy to your Property Manager (with your consent)",
                ].map((b, i) => (
                  <div key={i} className="flex items-start gap-2 text-slate-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 text-[13px] text-slate-700 leading-relaxed">
                <strong className="font-semibold text-slate-900">Terms</strong>:
                Renews automatically. Cancel any time — benefits continue until
                your paid-through date. No pro-rata refund on cancellation.
                Your card details are never shared with your Property Manager
                or your local PWS Partner. Report access remains governed by
                your existing sharing permissions.
              </div>
            </div>

            {error && (
              <div data-testid="lwh-enrol-error"
                    className="mt-4 rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">
                {String(error)}
              </div>
            )}

            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="lwh-enrol-consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-sm text-slate-800">
                I understand the membership is <strong>{centsToAud(price.amount_cents)}/year</strong>
                &nbsp;per property, renews automatically, and can be cancelled any
                time (benefits continue until the paid-through date). Laboratory
                tests are charged separately.
              </span>
            </label>

            <button
              type="button"
              data-testid="lwh-enrol-submit"
              disabled={!consent || submitting}
              onClick={submit}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 shadow-sm"
            >
              {submitting ? "Preparing checkout…" : "Continue to secure payment"}
            </button>
            <p className="mt-2 text-[11px] text-slate-500 text-center">
              You&apos;ll be redirected to Stripe for payment. Membership becomes
              active only after Stripe confirms payment — the confirmation email
              is your source of truth.
            </p>
          </>
        )}
      </div>
    </LandlordShell>
  );
}

function UnavailablePanel({ testId }) {
  return (
    <div data-testid={testId}
          className="mt-8 rounded-3xl border border-slate-200 bg-white p-8 text-center">
      <Info className="w-8 h-8 text-slate-400 mx-auto mb-3" />
      <div className="font-display font-semibold text-slate-900">Enrolment is not currently available</div>
      <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
        The AWARE Water Health Program is being finalised in your region.
        Please check back shortly, or contact support if you were expecting
        to see enrolment here.
      </p>
    </div>
  );
}

// Success page — informational only, does NOT grant benefits.
export function LandlordWaterHealthSuccess() {
  return (
    <LandlordShell>
      <div data-testid="lwh-enrol-success-page"
            className="max-w-lg mx-auto px-4 sm:px-6 py-10 text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
        <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">
          Payment received — activating your membership
        </h1>
        <p className="mt-2 text-slate-600 text-sm leading-relaxed">
          Stripe is confirming your payment with us. Your membership will
          appear as Active in your portal within a minute. We&apos;ll email
          you the receipt shortly.
        </p>
        <a href="/landlord/portal" data-testid="lwh-enrol-success-portal-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-5 py-2.5">
          Return to portal
        </a>
      </div>
    </LandlordShell>
  );
}
